import { upload } from "@/apis/common";
import {
  buyerAppeals,
  buyerCancelsOrder,
  buyerConfirmsOrderHasBeenPaid,
  getCancelsOrderDailyCount,
  getCurrentOrderStatus,
  myOrderDetail,
  getPaymentOrderOrderId,
} from "@/apis/market";
import { useConfigContext } from "@/app";
import { INTERNET_DELAY, OrderStatus } from "@/constants";
import { StateReducer, useCreateReducer } from "@/libs/reducer";
import { useConfigStore } from "@/stores";
import { useCryptoMerchant } from "@/stores/CryptoTradingStore";
import { ErPayment, Order } from "@/types/Api/Market/Order/myOrderDetail";
import { useLatest, useLockFn, useRequest } from "ahooks";
import { Toast } from "antd-mobile";
import { useCallback, useEffect,useState,useRef } from "react";
import { useParams } from "react-router-dom";
import * as Sentry from '@sentry/react';
/**
 * 使用 Turn Interface to Object 插件去直接成 pagestate
 */
interface PageState {
  /** 订单信息 */
  order?: Order;
  /** 卖家付款信息 */
  sellerPayment?: ErPayment;
  /** 买家付款信息 */
  buyerPayment?: ErPayment;
  /** 订单倒计时 */
  countdown: number;
  /** 订单状态 */
  status: OrderStatus;
  /** 是否超时订单 */
  isTimeOut: boolean;

  /** 上传的转账凭证 */
  voucher: string;
}

const pagestate = {
  order: undefined,
  sellerPayment: undefined,
  buyerPayment: undefined,
  countdown: 0,
  status: OrderStatus.BUYER_REQUEST,
  isTimeOut: false,
  voucher: "",
};

type ActionType =
  | "update-order"
  | "refresh-order"
  | "finish-countdown"
  | "update-voucher"
  | "update-status"
  | "apply-arbitrate";

const stateReducer: StateReducer<PageState, ActionType> = (state, action) => {
  switch (action.type) {
    case "update-order":
      return Object.assign({}, state, {
        countdown: action.countdown,
        order: action.order,
        status: action.status as OrderStatus,
        isTimeOut: action.isTimeOut,
        sellerPayment: action.sellerPayment,
        buyerPayment: action.buyerPayment,
      });
    case "refresh-order":
      return Object.assign({}, state, {
        status: action.status as OrderStatus,
        isTimeOut: action.isTimeOut,
      });
    case "finish-countdown":
      return Object.assign({}, state, {
        countdown: 0,
      });
    case "update-voucher": {
      return Object.assign({}, state, {
        voucher: action.voucher,
      });
    }
    case "update-status":
      return Object.assign({}, state, {
        status: action.status,
      });

    default:
      return state;
  }
};

export const useSetup = () => {
  const params = useParams();
  const { getSourcePath } = useConfigContext();
  const cryptoMerchantStore = useCryptoMerchant();
  const [
    waitingSellerCompletesOrderDelayTime,
    waitingSellerConfirmDelayTime,
    buyerPaysOvertimeDelayTime,
  ] = useConfigStore((state) => [
    state.waitingSellerCompletesOrderDelayTime,
    state.waitingSellerConfirmDelayTime,
    state.buyerPaysOvertimeDelayTime,
  ]);
  const [state, dispatch] = useCreateReducer<PageState, ActionType>(
    stateReducer,
    pagestate,
    {}
  );
  const latestState = useLatest(state);
  const scollableRef = useRef<any>(null);
  const [ticking, setTicking] = useState<boolean>(false);
  const [isBottom, setIsBottom] = useState<boolean>(false);

  /* `loadStatus` 函数负责从服务器获取订单的当前状态。它使用“orderId”参数对“getCurrentOrderStatus”进行 API
  调用。如果API调用成功，则会更新state中的订单状态。如果订单存在争议，则会将状态更新为“OrderStatus.DISPUTING”。如果订单状态发生变化，它会调用“load”函数来获取更新后的订单详细信息。 */
  const loadStatus = async () => {
    try {
      const response = await getCurrentOrderStatus({
        orderId: Number(params.orderId),
      }, {sellerType: cryptoMerchantStore.BuyerApiPath});
      if (response.ok) {
        const oldStatus = latestState.current.status;
        const extend = {
          isTimeOut: latestState.current.isTimeOut,
          status: latestState.current.status,
        };
        // 争议订单
        if (latestState.current.order && latestState.current.order.appeal > 0) {
          latestState.current.status = OrderStatus.DISPUTING;
          if (response.data!.status === OrderStatus.TIME_OVER) {
            extend.isTimeOut = true;
          }
        } else {
          extend.status = response.data!.status;
        }

        dispatch({ type: "refresh-order", ...extend });

        if (extend.status !== oldStatus) {
          load();
        }
      }
    } catch (error) {
      Sentry.captureMessage(
        `fn:CurrentOrderStatus, orderId: ${params.orderId}`,
        "info"
      );
      console.error(error);
      // TODO: 不上报错误，仅上报日志
    
    }
  };

  const { run, cancel } = useRequest(loadStatus, {
    pollingInterval: 10 * 1000,
    manual: true,
  });

  const refreshFun = ()=>{
    run();
    load();
  }

  /* “load”函数负责从服务器获取订单详细信息。它以“orderId”作为参数，并调用 API
  来检索订单信息。如果API调用成功，它会使用获取的数据更新状态，包括订单详细信息、卖家付款信息、买家付款信息、倒计时、订单状态以及订单是否超时。如果 API 调用失败，则返回 false。 */
  const { refresh: load, runAsync: asyncLoad } = useRequest(
    async () => {
      const response = await myOrderDetail({ orderId: Number(params.orderId) },{
        sellerType: cryptoMerchantStore.BuyerApiPath
      });

      if (response.ok) {
        const serverTimestamp =
          Date.now() + Number(window.localStorage.getItem(INTERNET_DELAY));

        const extend = {
          countdown: 0,
          order: response.data!.order,
          sellerPayment: response.data!.sellerPayment,
          buyerPayment: response.data!.buyerPayment,
          status: response.data!.order.status as OrderStatus,
          isTimeOut: false,
        };

        if (response.data!.currentStateExpireTime) {
          if (response.data!.currentStateExpireTime.expire) {
            extend.countdown = response.data!.currentStateExpireTime
              ? Number(response.data!.currentStateExpireTime.expire) * 1000 -
                serverTimestamp
              : 0;
          }
        }

        if (response.data!.order.appeal > 0) {
          extend.status = OrderStatus.DISPUTING;
          if (response.data!.order.status === OrderStatus.TIME_OVER) {
            extend.isTimeOut = true;
          }
        }

        if (
          extend.status === OrderStatus.TIME_OVER ||
          extend.status === OrderStatus.SELLER_CANCELED_DEAL ||
          extend.status === OrderStatus.CONFIRMED_BY_OFFICIAL ||
          extend.status === OrderStatus.CANCELED_BY_OFFICIAL ||
          extend.status === OrderStatus.DEAL
        ) {
          cancel();
        }

        // 提交状态
        await dispatch({
          type: "update-order",
          ...extend,
        });
      } else {
        // TODO: 处理请求失败
      }
    },
    {
      manual: true,
    }
  );

  /**
   * 上传转账截图凭证
   * @param file
   */
  const uploadPaidScreenShot = async (file: File) => {
    const form = new FormData();
    form.append("imgFile", file);
    form.append("type", "tradeImg");
    Toast.show({
      icon: 'loading',
      content: '上传中…',
      duration: 0,
      maskClickable: false,
    })
    const response = await upload(form);
    if (response.ok) {
      Toast.clear()
      dispatch({ type: "update-voucher", voucher: response.data!.name });
      load();
    } else {
      Toast.clear()
      Toast.show(response.msg);
      // TODO: 上报上传失败
      Sentry.captureException(
        new Error(`上传转账截图失败, size: ${file?.size}`),
      );
    }
  };

  /* `confirmPaid` 函数负责确认买家已支付订单费用。它使用订单 ID 和凭证（传输屏幕截图）作为参数对“buyerConfirmsOrderHasBeenPaid”端点进行 API
  调用。如果 API 调用成功，该函数将调度一个操作以将订单状态更新为“OrderStatus.TRANSFERRED”。如果 API 调用失败，则会处理错误（TODO：处理错误）。 */
  const confirmPaid = useCallback(async () => {
    const response = await buyerConfirmsOrderHasBeenPaid({
      orderId: Number(params.orderId),
      voucher: latestState.current.voucher,
    },{
      sellerType: cryptoMerchantStore.BuyerApiPath
    });

    if (response.ok) {
      dispatch({ type: "update-status", status: OrderStatus.TRANSFERRED });

    } else {
      Toast.show(response.msg);
      // TODO: 上报失败
      Sentry.captureException(
        new Error(`确认支付失败：orderId:${params.orderId}`),
      );
    }
  }, [dispatch, latestState, params.orderId]);

  const checkCancelable = useLockFn(async () => {
    const response = await getCancelsOrderDailyCount({},{
      sellerType: cryptoMerchantStore.BuyerApiPath
    });
    if (response.ok) {
      return response.data!;
    } else {
      // TODO: 提示重试
      Toast.show(response.msg);
    }
  });

  const confirmCancelTrading = useLockFn(async () => {
    const response = await buyerCancelsOrder({
      orderId: Number(params.orderId),
    }, {sellerType:cryptoMerchantStore.BuyerApiPath});
    if (response.ok) {
      dispatch({
        type: "update-status",
        status: OrderStatus.BUYRER_CANCELD_DEAL,
      });
      Toast.show(response.msg);
      load();
    } else {
      Toast.show(response.msg);
    }
    return response.ok;
  });

  const applyArbitrate = useLockFn(async (remark: string) => {
    const response = await buyerAppeals({
      remark,
      orderId: Number(params.orderId),
    },{
        sellerType:cryptoMerchantStore.BuyerApiPath
    });
    if (response.ok) {
      dispatch({ type: "apply-arbitrate", status: OrderStatus.DISPUTING });
      load();
    } else {
      // TODO: 提示重试
      Toast.show(response.msg);
    }
    return response.ok;
  });

  const jump2Pay = async () =>{
      let newPage:any;
      if(!window.plus){
        newPage =  window.open('',"支付页面") || window;
      }
      const r = await getPaymentOrderOrderId({orderId: Number(params.orderId)})
      if( r.ok ){
        if( !r?.data?.sellerPayment?.payUrl ){
          Toast.show('跳转支付地址错误');
          return;
        }
        if(!!window.plus){
          window.plus.runtime.openURL(r?.data?.sellerPayment?.payUrl);
        }else{
          newPage.location = r?.data?.sellerPayment?.payUrl;
        }
      }else{
        Toast.show(r.msg);
        newPage && newPage.close();
      }
  }

  const handleScroll = (layout:any)=>{
    if (layout.scrollHeight - layout.scrollTop === layout.clientHeight) {
      console.log("滚动到底部");
      setIsBottom(true);
    }
  }
  const scrollFun = ()=>{
    if (!ticking) {
      window.requestAnimationFrame(() => {
        handleScroll(scollableRef.current);
        setTicking(false);
      });
      setTicking(true);
    }
  }
  
  useEffect(() => {
    asyncLoad().then(run);
    return () => {
      cancel();
      scollableRef.current && scollableRef.current.removeEventListener("scroll", scrollFun);//卸载滚动事件
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if( state.status === OrderStatus.WAITING_FOR_TRANSFER && scollableRef.current){ // 等待买家付款  向下滚动监听
      scollableRef.current.addEventListener("scroll",scrollFun);
    }
  },[scollableRef.current])


  return {
    state,
    dispatch,
    orderDurations: {
      waitingSellerCompletesOrderDelayTime,
      waitingSellerConfirmDelayTime,
      buyerPaysOvertimeDelayTime,
    },
    getSourcePath,
    uploadPaidScreenShot,
    confirmPaid,
    checkCancelable,
    confirmCancelTrading,
    applyArbitrate,
    run,
    jump2Pay,
    scollableRef,
    isBottom,
    refreshFun,
  };
};
